import { useState, useCallback } from "react";
import { CampaignTracking } from "../../../utils/CampaignTracking";
import {
  captureLeadAttempt,
  createCaptureId,
  outcomeForStatus,
  reportCaptureOutcome,
} from "../../../utils/leadCapture";
import { serializeFormData } from "../utils/formDataSerializer";
import { interpolate } from "../../../utils/interpolate/interpolate";
import { getUnitDisplayName } from "../../../utils/unit";
import {
  openExternalTab,
  reserveExternalTab,
  type ReservedExternalTab,
} from "../../../utils/externalTab";
import { isSafariUserAgent } from "../../../utils/userAgent";
import type { DynamicStructure } from "../../../utils/interpolate/types";
import { useSubmitGuard } from "../../../hooks/useSubmitGuard";
import type { DynamicFieldItem } from "../FormBuilder.types";

const CAPTURE_SOURCE = "FormBuilder";

interface SendCustomEndpointParams {
  customSubmitUrl: string;
  crmSubmitUrl: string;
  payload: Record<string, any>;
}

const sendCustomEndpoint = async ({
  customSubmitUrl,
  crmSubmitUrl,
  payload,
}: SendCustomEndpointParams) => {
  const url = customSubmitUrl?.trim() || "";

  if (!url || url === crmSubmitUrl) {
    return;
  }

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.error(`[FormBuilder] Custom endpoint returned ${res.status}`);
    }
  } catch (err) {
    console.error("[FormBuilder] Failed to send data to custom endpoint:", err);
  }
};

export interface AfterRedirectConfig {
  active?: boolean;
  url?: string;
  type?: "page" | "external_url" | "scroll_to_block";
  pageSlug?: string;
  openInNewTab?: boolean;
  scrollPageSlug?: string;
  blockId?: string;
}

export interface UseFormSubmitOptions {
  submitUrl?: string;
  afterRedirect?: AfterRedirectConfig;
  dynamicFields?: DynamicFieldItem[];
  dynamicStructure?: DynamicStructure;
  formId?: string;
  getAntiBotToken?: () => Promise<string | null>;
  onSuccess?: (data: Record<string, any>) => void;
  onError?: (error: Error) => void;
  onStart?: () => void;
}

export const useFormSubmit = (options: UseFormSubmitOptions) => {
  const {
    submitUrl,
    afterRedirect,
    dynamicFields,
    dynamicStructure,
    formId,
    getAntiBotToken,
    onSuccess,
    onError,
    onStart,
  } = options;
  const crmSubmitUrl = process.env.NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL || "";

  const [serverError, setServerError] = useState(false);
  const { isSubmitting, runExclusive } = useSubmitGuard();

  const submitForm = useCallback(
    async (
      e: React.FormEvent<HTMLFormElement>,
      validateFields: (form: HTMLFormElement) => boolean,
      originUrl: string,
      addressData?: { city?: string; state?: string },
    ) => {
      e.preventDefault();

      const form = e.currentTarget as HTMLFormElement;
      const formData = new FormData(form);
      const data = serializeFormData(formData, addressData);
      const captureId = createCaptureId();

      captureLeadAttempt({
        captureId,
        source: CAPTURE_SOURCE,
        rawPayload: data,
        pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
        formId,
        visitorId:
          typeof data.af_visitor_id === "string" ? data.af_visitor_id : undefined,
      });

      if (!validateFields(form)) {
        reportCaptureOutcome({
          captureId,
          outcome: "REJECTED_CLIENT_VALIDATION",
        });
        return;
      }

      await runExclusive({
        task: async () => {
          setServerError(false);

          onStart?.();

          const wantsNewTab = Boolean(
            afterRedirect?.active &&
            (afterRedirect.type || "external_url") === "external_url" &&
            afterRedirect.url &&
            afterRedirect.openInNewTab,
          );

          const reservedTab: ReservedExternalTab | null =
            wantsNewTab && isSafariUserAgent()
              ? reserveExternalTab({ placeholderMessage: "Redirecionando..." })
              : null;

          try {
            // Envia o nome da unidade escolhida junto do id (campo `unit`). Necessário
            // no caminho de webhook específico, que recebe este payload cru e não passa
            // pelo hosting (que só adiciona unit_name na integração nativa).
            const unitId = data.unit;
            const selectedUnit =
              typeof unitId === "string" && unitId
                ? dynamicStructure?.channel?.units?.find((u) => u.id === unitId)
                : undefined;
            const unitName = selectedUnit
              ? getUnitDisplayName({ unit: selectedUnit })
              : "";

            const campaignTracking = CampaignTracking.getTrackingData();
            const payload: Record<string, any> = {
              ...data,
              ...(unitName ? { unit_name: unitName } : {}),
              ...(Object.keys(campaignTracking).length > 0
                ? { campaign: campaignTracking }
                : {}),
              // O formulário é a isca que capturou o lead (mesma estrutura `bait` do
              // WhatsApp): viaja em customParameters, sem coluna dedicada na conversão.
              ...(formId ? { bait: { type: "form", id: formId } } : {}),
              af_capture_id: captureId,
            };

            if (dynamicFields?.length) {
              const customParameters: Record<string, string | null> = {};
              for (const field of dynamicFields) {
                if (!field.key || !field.value) continue;
                if (field.value.includes("{{")) {
                  if (dynamicStructure) {
                    const result = interpolate({
                      structure: dynamicStructure,
                      template: field.value,
                    }).text;
                    // Token not resolved (still contains {{) or empty → null
                    customParameters[field.key] =
                      typeof result === "string" &&
                      result !== "" &&
                      !result.includes("{{")
                        ? result
                        : null;
                  } else {
                    // No dynamicStructure available → token can't resolve
                    customParameters[field.key] = null;
                  }
                } else {
                  customParameters[field.key] = field.value || null;
                }
              }
              if (Object.keys(customParameters).length > 0) {
                payload.customParameters = customParameters;
              }
            }

            if (getAntiBotToken) {
              const antiBotToken = await getAntiBotToken();
              if (antiBotToken) {
                payload.antiBotToken = antiBotToken;
              }
            }

            sendCustomEndpoint({
              customSubmitUrl: submitUrl || "",
              crmSubmitUrl,
              payload,
            });

            const response = await fetch(crmSubmitUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            });

            if (response.ok) {
              reportCaptureOutcome({ captureId, outcome: "ACCEPTED_QUEUED" });

              if (afterRedirect?.active) {
                const redirectType = afterRedirect.type || "external_url";

                switch (redirectType) {
                  case "page":
                    if (afterRedirect.pageSlug) {
                      const slug = afterRedirect.pageSlug.startsWith("/")
                        ? afterRedirect.pageSlug
                        : `/${afterRedirect.pageSlug}`;
                      window.location.href = slug;
                      return;
                    }
                    break;
                  case "external_url":
                    if (afterRedirect.url) {
                      if (!afterRedirect.openInNewTab) {
                        window.location.href = afterRedirect.url;
                        return;
                      }

                      if (reservedTab) reservedTab.navigate(afterRedirect.url);
                      else openExternalTab({ url: afterRedirect.url });
                    }
                    break;
                  case "scroll_to_block":
                    if (afterRedirect.blockId) {
                      if (afterRedirect.scrollPageSlug) {
                        const scrollSlug =
                          afterRedirect.scrollPageSlug.startsWith("/")
                            ? afterRedirect.scrollPageSlug
                            : `/${afterRedirect.scrollPageSlug}`;
                        window.location.href = `${scrollSlug}#${afterRedirect.blockId}`;
                      } else {
                        const element = document.getElementById(
                          afterRedirect.blockId,
                        );
                        element?.scrollIntoView({ behavior: "smooth" });
                      }
                      return;
                    }
                    break;
                }
              }

              form.dispatchEvent(
                new CustomEvent("conversion", { detail: payload }),
              );

              onSuccess?.(payload);
            } else {
              reportCaptureOutcome({
                captureId,
                ...outcomeForStatus(response.status),
              });
              reservedTab?.cancel();
              setServerError(true);
              onError?.(new Error(`Server returned ${response.status}`));
            }
          } catch (error) {
            reportCaptureOutcome({
              captureId,
              outcome: "TRANSPORT_FAILED",
              outcomeDetail: error instanceof Error ? error.message : undefined,
            });
            reservedTab?.cancel();
            setServerError(true);
            onError?.(
              error instanceof Error ? error : new Error("Unknown error"),
            );
          }
        },
      });
    },
    [
      runExclusive,
      submitUrl,
      crmSubmitUrl,
      afterRedirect,
      dynamicFields,
      dynamicStructure,
      formId,
      getAntiBotToken,
      onSuccess,
      onError,
      onStart,
    ],
  );

  return {
    isSubmitting,
    serverError,
    submitForm,
  };
};
